// Helpers for the v3 pStats endpoint (/v3/stat-samples/query).
//
// Stat samples arrive as a JSON:API-ish envelope whose attribute names are not
// known ahead of time, so everything here discovers fields instead of hard
// coding them. Shared by the Device Table panel (via the device epics) and the
// Reports charts.

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

// Unwrap whichever list key the endpoint uses for the samples.
export const unwrapStatSamples = (rawResponse) => {
  let response = rawResponse;
  if (typeof response === "string") {
    try {
      response = JSON.parse(response);
    } catch (e) {
      return [];
    }
  }
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.content)) return response.content;
  if (Array.isArray(response?.samples)) return response.samples;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

// Flatten a sample's nested attributes into "a.b" column keys so consumers can
// render fields they do not know about ahead of time.
export const flattenStatSample = (source, prefix = "", target = {}) => {
  Object.keys(source || {}).forEach((key) => {
    const value = source[key];
    const columnKey = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      flattenStatSample(value, columnKey, target);
    } else {
      target[columnKey] = Array.isArray(value) ? value.join(", ") : value;
    }
  });
  return target;
};

export const flattenStatSamples = (response) =>
  unwrapStatSamples(response).map((sample) =>
    flattenStatSample(sample?.attributes ? { ...sample.attributes } : { ...sample })
  );

// Shape stat samples like the devicedata-advanced rows the Device Table renders.
export const toStatSampleRows = (response) => {
  const samples = flattenStatSamples(response);

  const devicePropertyCodes = samples.reduce(
    (acc, sample) => [...acc, ...Object.keys(sample).filter((key) => !acc.includes(key))],
    []
  );

  const dataTypes = devicePropertyCodes.map((code) => {
    const sample = samples.find((row) => row[code] !== null && row[code] !== undefined && row[code] !== "");
    return typeof sample?.[code] === "string" && ISO_DATE_PATTERN.test(sample[code])
      ? "DateAndTime"
      : "String";
  });

  const data = samples.map((sample, index) => ({
    deviceDataId: `pStats-${index}`,
    // Stat samples are keyed by device, not by owner; the table fills the
    // Resident Name in from the IDENTITY panel using the row's MAC.
    owner: { userId: null, fullName: "" },
    data: devicePropertyCodes.reduce((acc, code) => {
      const value = sample[code];
      return { ...acc, [code]: { value: value === null || value === undefined ? "" : value } };
    }, {}),
  }));

  return { devicePropertyCodes, dataTypes, data };
};

// "data.HrMin.value" / "hr_min" / "HrMin" all normalize to "hrmin", so a sample
// can be probed with one set of matchers regardless of the naming the endpoint
// happens to use. A trailing wrapper segment ("...HrMin.value") is skipped so
// the reading's own name is what gets matched.
const GENERIC_LEAF_SEGMENT = /^(value|val|v|data|attributes)$/;

const normalizeStatKey = (key) => {
  const segments = String(key)
    .split(".")
    .map((segment) => segment.replace(/[^a-z0-9]/gi, "").toLowerCase())
    .filter(Boolean);
  const leaf = segments[segments.length - 1] || "";
  return segments.length > 1 && GENERIC_LEAF_SEGMENT.test(leaf)
    ? segments[segments.length - 2]
    : leaf;
};

const findStatKey = (sample, matcher) =>
  Object.keys(sample || {}).find((key) => matcher.test(normalizeStatKey(key)));

const readStatNumber = (sample, matcher) => {
  const key = findStatKey(sample, matcher);
  if (!key) return null;

  const raw = sample[key];
  // Only an absent field is "no reading" - Number(null) is 0, so null and ""
  // have to be rejected before the conversion.
  if (raw === null || raw === undefined || raw === "") return null;

  const value = Number(raw);
  return Number.isNaN(value) ? null : value;
};

const STAT_MATCHERS = {
  hrMin: /^(hr|heartrate)(min|minimum)$|^(min|minimum)(hr|heartrate)$/,
  hrAvg: /^(hr|heartrate)(avg|average|mean)$|^(avg|average|mean)(hr|heartrate)$/,
  hrMax: /^(hr|heartrate)(max|maximum)$|^(max|maximum)(hr|heartrate)$/,
  rrMin: /^(rr|respirationrate|resprate)(min|minimum)$|^(min|minimum)(rr|respirationrate|resprate)$/,
  rrAvg: /^(rr|respirationrate|resprate)(avg|average|mean)$|^(avg|average|mean)(rr|respirationrate|resprate)$/,
  rrMax: /^(rr|respirationrate|resprate)(max|maximum)$|^(max|maximum)(rr|respirationrate|resprate)$/,
};

const TIMESTAMP_MATCHER = /timestamp|providedon|recordedat|reportedat|sampledat|createdat|^date$|^time$|^day$/;

// The timestamp column is whichever date-ish field actually holds a parseable
// date; fall back to any parseable date field so a renamed column still works.
export const readStatTimestamp = (sample) => {
  const parse = (key) => {
    const value = sample?.[key];
    if (value === null || value === undefined || value === "") return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const named = Object.keys(sample || {}).find(
    (key) => TIMESTAMP_MATCHER.test(normalizeStatKey(key)) && parse(key)
  );
  if (named) return parse(named);

  const anyDate = Object.keys(sample || {}).find(
    (key) => typeof sample[key] === "string" && ISO_DATE_PATTERN.test(sample[key])
  );
  return anyDate ? parse(anyDate) : null;
};

// Reduce a raw stat-samples response to { timestamp, hr, rr } entries the
// Reports charts and CSV work from. Only samples with no usable timestamp, or
// with no HR/RR fields at all, are dropped - zeros are kept.
export const toVitalsStatEntries = (response) =>
  flattenStatSamples(response).reduce((acc, sample) => {
    const timestamp = readStatTimestamp(sample);
    if (!timestamp) return acc;

    // A zero is a real reading, not a missing one: a day the device reported
    // 0 bpm still belongs on the chart. Only a field that is absent counts as
    // no data, and any one of min/avg/max is enough to plot the day.
    const build = (minMatcher, avgMatcher, maxMatcher) => {
      const min = readStatNumber(sample, minMatcher);
      const max = readStatNumber(sample, maxMatcher);
      const avg = readStatNumber(sample, avgMatcher);

      if (min === null && max === null && avg === null) return null;

      const low = min ?? avg ?? max;
      const high = max ?? avg ?? min;

      return { min: low, avg: avg ?? (low + high) / 2, max: high };
    };

    const hr = build(STAT_MATCHERS.hrMin, STAT_MATCHERS.hrAvg, STAT_MATCHERS.hrMax);
    const rr = build(STAT_MATCHERS.rrMin, STAT_MATCHERS.rrAvg, STAT_MATCHERS.rrMax);
    if (!hr && !rr) return acc;

    return [...acc, { timestamp: timestamp.toISOString(), hr, rr }];
  }, []);
